import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { translateBatch, getLangInfo } from '../../../lib/translate';

interface CaseNote {
  id: string;
  note: string;
  note_type: string;
  created_by: string;
  created_at: string;
}

interface Props {
  clientId: string;
  notes: CaseNote[];
  preferredLanguage?: string;
}

const NOTE_TYPE_STYLES: Record<string, { label: string; icon: string; color: string; bg: string; border: string }> = {
  update: {
    label: 'Case Update',
    icon: 'ri-file-list-3-line',
    color: 'text-[#FDB913]',
    bg: 'bg-[#FDB913]/10',
    border: 'border-[#FDB913]/30',
  },
  milestone: {
    label: 'Milestone',
    icon: 'ri-flag-2-fill',
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
  },
  action_required: {
    label: 'Action Required',
    icon: 'ri-alarm-warning-fill',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
  },
  info: {
    label: 'Information',
    icon: 'ri-information-fill',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
  },
  client_note: {
    label: 'My Note',
    icon: 'ri-edit-2-fill',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
  },
};

function formatNoteDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MAX_NOTE_LENGTH = 500;

export default function CaseNotes({ clientId, notes: initialNotes, preferredLanguage = 'en' }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [notes, setNotes] = useState<CaseNote[]>(initialNotes);
  const [translatedNotes, setTranslatedNotes] = useState<string[]>([]);
  const [translating, setTranslating] = useState(false);

  // Client note writing
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const langInfo = getLangInfo(preferredLanguage);

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  useEffect(() => {
    if (notes.length === 0 || preferredLanguage === 'en') {
      setTranslatedNotes([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setTranslating(true);
      const texts = notes.map((n) => n.note);
      const results = await translateBatch(texts, preferredLanguage, 'en');
      if (!cancelled) {
        setTranslatedNotes(results);
        setTranslating(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [notes, preferredLanguage]);

  const handleAddNote = async () => {
    if (!newNote.trim() || saving) return;
    if (newNote.length > MAX_NOTE_LENGTH) {
      setSaveError(`Note must be ${MAX_NOTE_LENGTH} characters or fewer.`);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const { data, error } = await supabase
        .from('case_notes')
        .insert({
          client_id: clientId,
          note: newNote.trim(),
          note_type: 'client_note',
          created_by: 'client',
          is_visible: true,
        })
        .select()
        .single();
      if (error) throw error;
      setNotes(prev => [data as CaseNote, ...prev]);
      setNewNote('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Notify client (portal notification)
      try {
        await supabase.from('client_notifications').insert({
          client_id: clientId,
          title: 'Note Saved',
          message: 'Your note has been saved and shared with your attorney.',
          notification_type: 'info',
          is_read: false,
        });
      } catch { /* non-fatal */ }

      // Notify attorney (admin email)
      try {
        // Get client info for the notification
        const { data: clientRow } = await supabase
          .from('clients')
          .select('name, email, company')
          .eq('id', clientId)
          .maybeSingle();
        if (clientRow) {
          await supabase.functions.invoke('send-admin-notification', {
            body: {
              eventType: 'client_note_added',
              clientName: (clientRow as { name: string; email: string; company: string | null }).name,
              clientEmail: (clientRow as { name: string; email: string; company: string | null }).email,
              company: (clientRow as { name: string; email: string; company: string | null }).company || '',
              noteContent: newNote.trim(),
              noteType: 'Client Note',
              timestamp: new Date().toISOString(),
            },
          });
        }
      } catch { /* non-fatal */ }
    } catch (err: unknown) {
      setSaveError((err as Error).message || 'Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-6 cursor-pointer"
      >
        <h3 className="text-2xl font-black text-white flex items-center gap-3">
          <i className="ri-sticky-note-2-line text-[#FDB913]"></i>
          Case Notes
          {notes.length > 0 && (
            <span className="bg-[#FDB913]/20 border border-[#FDB913]/40 text-[#FDB913] text-xs font-black rounded-full px-2.5 py-0.5">
              {notes.length}
            </span>
          )}
        </h3>
        <i className={expanded ? 'ri-arrow-up-s-line text-white/40 text-xl' : 'ri-arrow-down-s-line text-white/40 text-xl'}></i>
      </button>

      {expanded && (
        <>
          {/* Client note input */}
          <div className="mb-6 bg-violet-500/5 border border-violet-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-violet-500/20 border border-violet-500/30 rounded-lg flex-shrink-0">
                <i className="ri-edit-2-fill text-violet-400 text-sm"></i>
              </div>
              <p className="text-violet-400 font-black text-sm">Write a Note</p>
              <span className="text-white/30 text-xs">— shared with your attorney</span>
            </div>
            <textarea
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              maxLength={MAX_NOTE_LENGTH}
              rows={3}
              placeholder="Add a note about your case, questions for your attorney, or any relevant information..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-violet-500/50 text-sm resize-none transition-colors"
            />
            <div className="flex items-center justify-between mt-2">
              <span className={`text-xs ${newNote.length > MAX_NOTE_LENGTH * 0.9 ? 'text-yellow-400' : 'text-white/30'}`}>
                {newNote.length}/{MAX_NOTE_LENGTH}
              </span>
              <div className="flex items-center gap-2">
                {saveSuccess && (
                  <span className="text-green-400 text-xs font-bold flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill"></i>Note saved!
                  </span>
                )}
                <button
                  onClick={handleAddNote}
                  disabled={saving || !newNote.trim() || newNote.length > MAX_NOTE_LENGTH}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/30 text-violet-400 font-black text-xs rounded-lg transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {saving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : <><i className="ri-send-plane-fill"></i>Add Note</>}
                </button>
              </div>
            </div>
            {saveError && (
              <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>{saveError}
              </p>
            )}
          </div>

          {notes.length === 0 ? (
            <div className="text-center py-10">
              <i className="ri-chat-4-line text-white/20 text-5xl mb-3"></i>
              <p className="text-white/40 text-sm">No notes yet</p>
              <p className="text-white/25 text-xs mt-1">
                Write a note above or your attorney will post updates here as your case progresses
              </p>
            </div>
          ) : (
            <>
              {translating && (
                <div className="flex items-center gap-2 bg-[#FDB913]/10 border border-[#FDB913]/20 rounded-lg px-4 py-3 mb-4">
                  <div className="w-4 h-4 border-2 border-[#FDB913] border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                  <p className="text-[#FDB913] text-xs font-bold">
                    Translating notes to {langInfo.flag} {langInfo.label}...
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {notes.map((note, idx) => {
                  const style = NOTE_TYPE_STYLES[note.note_type] || NOTE_TYPE_STYLES.update;
                  const displayText =
                    preferredLanguage !== 'en' && translatedNotes[idx]
                      ? translatedNotes[idx]
                      : note.note;
                  return (
                    <div
                      key={note.id}
                      className={`${style.bg} ${style.border} border rounded-xl p-5`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-9 h-9 flex items-center justify-center ${style.bg} border ${style.border} rounded-lg flex-shrink-0 mt-0.5`}>
                          <i className={`${style.icon} ${style.color} text-base`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-black uppercase tracking-wider ${style.color}`}>
                              {style.label}
                            </span>
                            {preferredLanguage !== 'en' && translatedNotes[idx] && !translating && (
                              <span className="text-white/25 text-xs flex items-center gap-1">
                                <i className="ri-translate-2 text-[#FDB913]/60"></i>
                                {langInfo.flag}
                              </span>
                            )}
                          </div>
                          <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">
                            {displayText}
                          </p>
                          <p className="text-white/35 text-xs mt-3">
                            <i className="ri-time-line mr-1"></i>
                            {formatNoteDate(note.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
